import cls from "@fvilers/cls";
import { CloseDetailsIcon } from "@imi/icons/CloseDetailsIcon.js";
import { OpenDetailsIcon } from "@imi/icons/OpenDetailsIcon.js";
import { TabPanel, TabPanelProps } from "@imi/ui/components/Tabs/TabPanel.js";
import { Tabs, TabsProps } from "@imi/ui/components/Tabs/Tabs.js";
import { ComponentProps, use, useState } from "react";
import { useFieldContext } from "../../../hooks/useAppForm";
import { FilterTabsContext } from "./context/FilterTabsContext";
import { FilterTabsProvider } from "./context/FilterTabsProvider";
import styles from "./FilterTabs.module.css";

type FilterTabsProps = {
  defaultOpen?: boolean;
} & TabsProps<"div">;

type FilterTabsSummaryProps = {
  onToggle?: (open: boolean) => void;
} & Omit<ComponentProps<"div">, "onToggle">;

export function FilterTabs({
  className,
  defaultOpen,
  onTabChange,
  ...props
}: FilterTabsProps) {
  const [open, setOpen] = useState<boolean>(defaultOpen || true);
  return (
    <FilterTabsProvider open={open} setOpen={setOpen}>
      <Tabs
        className={cls(styles.FilterTabs, className)}
        data-open={open ? "true" : "false"}
        onTabChange={(value) => {
          if (!open) {
            setOpen(true);
          }
          onTabChange?.(value);
        }}
        {...props}
      />
    </FilterTabsProvider>
  );
}

export function FilterTabsField(props: FilterTabsProps) {
  const field = useFieldContext<string>();
  return (
    <FilterTabs
      {...props}
      onBlur={field.handleBlur}
      onTabChange={field.handleChange}
      value={field.state.value}
    />
  );
}

export function FilterTabsPanel({ className, ...props }: TabPanelProps) {
  return (
    <TabPanel className={cls(styles.FilterTabsPanel, className)} {...props} />
  );
}

export function FilterTabsPanels({
  children,
  className,
  ...props
}: ComponentProps<"div">) {
  const { open } = use(FilterTabsContext);

  return (
    <div
      className={cls(styles.FilterTabsPanels, className)}
      data-open={open ? "true" : "false"}
      data-slot="panels"
      {...props}
    >
      {children}
    </div>
  );
}

export function FilterTabsSummary({
  children,
  className,
  onToggle,
  ...props
}: FilterTabsSummaryProps) {
  const { open, setOpen } = use(FilterTabsContext);

  return (
    <div
      className={cls(styles.FilterTabsSummary, className)}
      data-open={open ? "true" : "false"}
      data-slot="summary"
      {...props}
    >
      {children}
      <button
        aria-label="Toggle filters"
        className={styles.ToggleButton}
        onClick={() => {
          onToggle?.(!open);
          setOpen(!open);
        }}
        type="button"
      >
        {open ? <CloseDetailsIcon /> : <OpenDetailsIcon />}
      </button>
    </div>
  );
}
